import { useState } from 'react'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'
import { ChevronDown } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import type {
  ColumnMode,
  DateColumn,
} from '@/features/reports/lib/order-proposal-math'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { COLUMN_PRESETS } from '@/features/reports/lib/order-proposal-math'
import {
  reportDaySpan,
  shiftReportDate,
} from '@/features/reports/lib/report-zone'
import { formatColumn } from '@/features/reports/lib/order-proposal-labels'
import { cn } from '@/lib/utils'

/** The server's limit per date column (D13). */
const MAX_COLUMN_DAYS = 62

/** `YYYY-MM-DD` as a local Date for the calendar, which works on the viewer's own calendar. */
export function toCalendarDate(date: string): Date {
  const [year, month, day] = date.split('-').map(Number)
  return new Date(year, month - 1, day)
}

export function fromCalendarDate(date: Date): string {
  return format(date, 'yyyy-MM-dd')
}

/**
 * A day or a von–bis range clicked on a calendar: the first click picks a day, a second one makes
 * it a range of at most `maxDays`. `take` hands over what was clicked (null when nothing was) and
 * starts afresh.
 */
export function useRangePick(maxDays: number) {
  const [pending, setPending] = useState<{
    from: string
    to: string
    complete: boolean
  } | null>(null)

  const pick = (day: string) => {
    if (!pending || pending.complete) {
      setPending({ from: day, to: day, complete: false })
      return
    }
    let from = day < pending.from ? day : pending.from
    const to = day < pending.from ? pending.from : day
    if (reportDaySpan(from, to) > maxDays) {
      from = shiftReportDate(to, -(maxDays - 1))
    }
    setPending({ from, to, complete: true })
  }

  const take = (): DateColumn | null => {
    if (!pending) return null
    setPending(null)
    return { from: pending.from, to: pending.to }
  }

  return { pending, pick, take, reset: () => setPending(null) }
}

/**
 * One date column's choice: a quick pick, or a day or a von–bis range on the calendar
 * (CHG-2026-10-bestellvorschlag). The first click picks a day, a second one makes it a range; the
 * choice applies when the popover closes.
 */
export function DateColumnPicker({
  mode,
  column,
  onChange,
}: {
  mode: ColumnMode
  column: DateColumn
  onChange: (mode: ColumnMode) => void
}) {
  const { t } = useTranslation('reports')
  const [open, setOpen] = useState(false)
  const range = useRangePick(MAX_COLUMN_DAYS)

  const close = (next: boolean) => {
    setOpen(next)
    const picked = next ? null : range.take()
    if (picked) onChange(picked)
  }

  const shown = range.pending ?? column

  return (
    <Popover open={open} onOpenChange={close}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1 border-primary/40 font-semibold text-primary"
        >
          {formatColumn(column)}
          <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="flex w-auto p-0" align="start">
        <div className="flex flex-col gap-1 border-r p-2">
          {COLUMN_PRESETS.map((preset) => (
            <Button
              key={preset}
              variant="ghost"
              size="sm"
              className={cn(
                'h-8 justify-start text-sm',
                mode === preset && !range.pending && 'bg-muted font-semibold',
              )}
              onClick={() => {
                range.reset()
                setOpen(false)
                onChange(preset)
              }}
            >
              {t(`orderProposal.presets.${preset}`)}
            </Button>
          ))}
        </div>
        <Calendar
          mode="range"
          captionLayout="dropdown"
          showWeekNumber
          selected={{
            from: toCalendarDate(shown.from),
            to: toCalendarDate(shown.to),
          }}
          onSelect={(_range, triggerDate) =>
            range.pick(fromCalendarDate(triggerDate))
          }
          locale={de}
          weekStartsOn={1}
          defaultMonth={toCalendarDate(shown.to)}
          startMonth={new Date(2020, 0)}
          endMonth={new Date(new Date().getFullYear() + 1, 11)}
        />
      </PopoverContent>
    </Popover>
  )
}
